import * as React from "react";
import { ActivityIndicator, TouchableOpacity } from "react-native";
import { AudioModule } from "expo-audio";
import { MaterialIcons } from "@expo/vector-icons";
import * as ScreenOrientation from "expo-screen-orientation";
import {
  VideoView,
  useVideoPlayer,
  StatusChangeEventPayload,
} from "expo-video";

import { isAndroid, isWeb } from "~/utils/platform";
import { IconSizes } from "~/components/icon";
import { GoogleCastButton } from "~/components/cast-button";
import { colors } from "~/styles/theme";
import { asyncLogEvent, events } from "~/utils/analytics";
import {
  FlamelinkFile,
  makeDownloadUrl,
  makeMediaUrl,
  MediaSize,
} from "~/state/flamelink";
import { useAppSelector } from "~/state/hooks";
import { getVideoQuality } from "~/state/settings";
import { getInternetSpeedKey, InternetSpeed } from "~/utils/internet-speed";
import { handleError } from "~/utils/logger";

import {
  DebugContainer,
  DebugText,
  ErrorText,
  Thumbnail,
  ThumbnailContainer,
  VideoContainer,
  VideoMask,
  ControlsContainer,
  videoStyles,
} from "./styles";
import { getFileUrlAndSpeed } from "./utils";
import { messages } from "./intl";

const VIDEO_DEBUG_ENABLED = false;

interface VideoProps {
  file?: string;
  isDownloaded?: boolean;
  fileData?: FlamelinkFile;
}

interface VideoComponentProps {
  videoSource: string;
  videoSpeed?: InternetSpeed;
  thumbnail: string | null;
}

export const VideoComponent: React.FC<VideoComponentProps> = ({
  videoSource,
  videoSpeed,
  thumbnail,
}) => {
  const [maskVisible, setMaskVisible] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [castButtonVisible, setCastButtonVisible] = React.useState(false);
  const [error, setError] = React.useState<boolean>(false);

  const player = useVideoPlayer(videoSource, (playerInstance) => {
    playerInstance.loop = false;
  });

  const handleVideoError = React.useCallback(
    (err?: Error | unknown) => {
      handleError(err);
      setError(true);
      setIsLoading(false);
      try {
        if (player && player.status !== "error") {
          player.pause();
        }
      } catch (e) {
        console.warn("Error during error handling cleanup:", e);
      }
    },
    [player]
  );

  const handleFullscreenUpdate = React.useCallback(async function (
    fullscreenUpdate: boolean
  ) {
    if (!isAndroid) {
      return;
    }

    try {
      if (fullscreenUpdate) {
        await ScreenOrientation.unlockAsync();
      } else {
        await ScreenOrientation.lockAsync(
          ScreenOrientation.OrientationLock.PORTRAIT
        );
      }
    } catch (e) {
      handleError(e);
    }
  },
  []);

  const onComplete = React.useCallback(() => {
    const fileName = videoSource?.split("/").pop();
    asyncLogEvent(events.VIDEO_COMPLETE, { fileName });
  }, [videoSource]);

  const onPlay = React.useCallback(() => {
    setMaskVisible(false);
    const fileName = videoSource?.split("/").pop();
    asyncLogEvent(events.VIDEO_PLAY, { fileName });
    try {
      if (player.status === "readyToPlay") {
        player.play();
      }
    } catch (err) {
      handleVideoError(err);
    }
  }, [videoSource, player, handleVideoError]);

  const onRetry = React.useCallback(() => {
    setError(false);
    setIsLoading(true);
    try {
      player.replay();
    } catch (err) {
      handleVideoError(err);
    }
    setMaskVisible(true);
  }, [player, handleVideoError]);

  React.useEffect(() => {
    let isMounted = true;

    const subscription = player.addListener(
      "statusChange",
      (payload: StatusChangeEventPayload) => {
        if (!isMounted) {
          return;
        }

        const status = payload.status;

        if (error && status !== "error") {
          setError(false);
        }

        switch (status) {
          case "idle":
            setIsLoading(true);
            setCastButtonVisible(false);
            break;
          case "loading":
            setIsLoading(true);
            setCastButtonVisible(false);
            break;
          case "readyToPlay":
            setIsLoading(false);
            if (!maskVisible && !player.playing) {
              player.play();
            }
            if (!player.playing) {
              setCastButtonVisible(true);
            }
            break;
          case "error":
            setIsLoading(false);
            setError(true);
            handleVideoError(
              payload.error || new Error("Unknown player error")
            );
            break;
        }

        if (player.playing && castButtonVisible) {
          setCastButtonVisible(false);
          setMaskVisible(false);
        } else if (
          !player.playing &&
          !castButtonVisible &&
          !maskVisible &&
          !error
        ) {
          setCastButtonVisible(true);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.remove();
      try {
        if (player && player.playing) {
          player.pause();
        }
      } catch (e) {
        handleError(e);
      }
    };
  }, [
    player,
    onComplete,
    castButtonVisible,
    error,
    handleVideoError,
    maskVisible,
  ]);

  return (
    <>
      {!isWeb ? (
        <ControlsContainer>
          <GoogleCastButton contentUrl={videoSource} />
        </ControlsContainer>
      ) : null}

      <VideoContainer>
        {isWeb ? (
          <video style={videoStyles} controls>
            <source src={videoSource} type="video/mp4" />
          </video>
        ) : (
          <>
            <VideoView
              player={player}
              style={videoStyles}
              contentFit="contain"
              nativeControls={true}
              onFullscreenEnter={() => handleFullscreenUpdate(true)}
              onFullscreenExit={() => handleFullscreenUpdate(false)}
            />

            {isLoading && !error ? (
              <VideoMask>
                <ActivityIndicator size="large" color={colors.white} />
              </VideoMask>
            ) : null}

            {maskVisible && !isLoading && !error ? (
              <>
                {thumbnail ? (
                  <ThumbnailContainer onPress={onPlay}>
                    <Thumbnail source={{ uri: thumbnail }} />
                  </ThumbnailContainer>
                ) : (
                  <VideoMask>
                    <TouchableOpacity onPress={onPlay}>
                      <MaterialIcons
                        name="play-circle-fill"
                        size={IconSizes.XLarge}
                        color={colors.white}
                      />
                    </TouchableOpacity>
                  </VideoMask>
                )}
              </>
            ) : error ? (
              <VideoMask>
                <TouchableOpacity onPress={onRetry}>
                  <MaterialIcons
                    name="replay"
                    size={IconSizes.XLarge}
                    color={colors.white}
                  />
                </TouchableOpacity>

                <ErrorText>{messages.error}</ErrorText>
              </VideoMask>
            ) : null}
          </>
        )}

        {VIDEO_DEBUG_ENABLED && (
          <DebugContainer>
            <DebugText>
              {videoSpeed
                ? `Speed: ${getInternetSpeedKey(videoSpeed as InternetSpeed)}`
                : ``}
            </DebugText>

            <DebugText>{`Video: ${videoSource}`}</DebugText>
          </DebugContainer>
        )}
      </VideoContainer>
    </>
  );
};

export const Video: React.FC<VideoProps> = ({
  file,
  isDownloaded,
  fileData,
}) => {
  const [videoSource, setVideoSource] = React.useState("");
  const [videoSpeed, setVideoSpeed] = React.useState<
    InternetSpeed | undefined
  >();
  const [thumbnail, setThumbnail] = React.useState<string | null>(null);
  const videoQuality = useAppSelector(getVideoQuality);

  const getVideoUrl = React.useCallback(
    async function () {
      let url: string | undefined;
      let speed: InternetSpeed | undefined;

      if (isDownloaded) {
        url = file;
      } else {
        const { fileUrl, speed: fileSpeed } = await getFileUrlAndSpeed(
          fileData,
          videoQuality
        );
        if (fileUrl) url = makeDownloadUrl(fileUrl);
        if (fileSpeed) speed = fileSpeed;
      }

      if (url) {
        setVideoSource(url);
      }
      if (speed) {
        setVideoSpeed(speed);
      }
    },
    [file, fileData, isDownloaded, videoQuality]
  );

  const enableAudio = async () => {
    try {
      await AudioModule.setAudioModeAsync({
        allowsRecording: false,
        interruptionMode: "doNotMix",
        playsInSilentMode: true,
        shouldPlayInBackground: false,
      });
    } catch (err) {
      handleError(err);
    }
  };

  const loadThumbnail = React.useCallback(async () => {
    const previewUri = fileData?.preview?.file
      ? makeMediaUrl(fileData.preview.file, MediaSize.Small)
      : undefined;

    if (previewUri) setThumbnail(previewUri);
  }, [fileData?.preview?.file]);

  React.useEffect(() => {
    getVideoUrl();
    loadThumbnail();
    enableAudio();
  }, [getVideoUrl, loadThumbnail]);

  if (!videoSource) return null;

  return (
    <VideoComponent
      videoSource={videoSource}
      videoSpeed={videoSpeed}
      thumbnail={thumbnail}
    />
  );
};
